import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../api";
import {
  Bot, Flame, Trophy, Loader2, CalendarCheck2, AlertTriangle, Code2, Sparkles,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";
import CoeLabCardLink from "../components/CoeLabCardLink";

/* ---------------------------------------------------------- daily quiz */
function DailyQuiz() {
  const [q, setQ] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/sprint/today").then(r => setQ(r.data)).catch(() => setQ(false));
  }, []);
  const answer = async (ans) => {
    setBusy(true);
    try {
      const r = await api.post("/sprint/today/answer", { challenge_id: q.id, answer: ans });
      setResult(r.data);
    } catch (e) { setResult({ error: formatError(e) }); }
    finally { setBusy(false); }
  };
  if (q === false) return null;
  return (
    <div className="card card-pad" data-testid="daily-quiz-card">
      <span className="eyebrow inline-flex items-center gap-1"><CalendarCheck2 size={12} className="text-signal" /> Daily quiz · 100-Day Skill Sprint</span>
      {q === null ? <div className="text-muted text-sm mt-3">Loading…</div> : (
        <>
          <div className="mt-2 flex items-center gap-2 text-[11px] text-muted">
            <span className="font-mono text-signal">{q.coe_lab}</span> · <span className="capitalize">{q.difficulty}</span>
          </div>
          <p className="font-display font-bold text-lg mt-1" data-testid="quiz-question">{q.question}</p>
          {q.already_answered && !result ? (
            <div className={`mt-3 text-sm font-semibold ${q.correct ? "text-green-700" : "text-amber-700"}`} data-testid="quiz-done">
              {q.correct ? "✓ You nailed today's question — come back tomorrow!" : "You've attempted today's question. Try again tomorrow!"}
            </div>
          ) : result ? (
            <div className="mt-3" data-testid="quiz-result">
              {result.error ? <div className="text-sm text-red-600">{result.error}</div> : (
                <>
                  <div className={`text-sm font-semibold ${result.correct ? "text-green-700" : "text-red-600"}`}>
                    {result.correct ? "✓ Correct!" : `✗ Not quite — answer: ${result.expected}`}
                  </div>
                  <div className="text-xs text-muted mt-1 inline-flex items-center gap-1"><Flame size={12} className="text-orange-500" /> Streak: {result.streak} day{result.streak === 1 ? "" : "s"}</div>
                </>
              )}
            </div>
          ) : (
            <div className="mt-3 flex gap-2 flex-wrap">
              {(q.type === "mcq" ? q.options : ["True", "False"]).map(opt => (
                <button key={opt} className="btn-secondary" disabled={busy}
                        onClick={() => answer(q.type === "mcq" ? opt : opt.toLowerCase())}
                        data-testid={`quiz-opt-${opt}`}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- streaks */
function StreakCard() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/sprint/me").then(r => setMe(r.data)).catch(() => setMe(false));
  }, []);
  if (!me) return null;
  return (
    <div className="card card-pad" data-testid="streak-card">
      <span className="eyebrow inline-flex items-center gap-1"><Flame size={12} className="text-orange-500" /> Streak & XP</span>
      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
        <div><div className="font-display font-black text-3xl text-orange-500" data-testid="current-streak">{me.current_streak}</div><div className="text-[11px] text-muted">Current streak</div></div>
        <div><div className="font-display font-black text-3xl">{me.longest_streak}</div><div className="text-[11px] text-muted">Longest</div></div>
        <div><div className="font-display font-black text-3xl text-signal">{me.total_correct * 10}</div><div className="text-[11px] text-muted">XP points</div></div>
      </div>
      <div className="mt-4 flex gap-[3px] flex-wrap" title="Last 30 days">
        {me.last_30_days.map(d => (
          <span key={d.date}
                className={`h-3.5 w-3.5 rounded-[3px] ${d.correct ? "bg-green-500" : d.attempted ? "bg-amber-300" : "bg-rule"}`} />
        ))}
      </div>
      <div className="text-[10px] text-muted mt-1">Last 30 days · green = correct</div>
    </div>
  );
}

/* ---------------------------------------------------------- study plan */
function StudyPlan() {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/coach/study-plan").then(r => setPlan(r.data.plan)).catch(() => {});
  }, []);
  const gen = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/coach/study-plan");
      setPlan(r.data.plan);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };
  const TYPE_COLOR = { learn: "#006CFF", practice: "#16A34A", revise: "#9933FF", assess: "#EA580C" };
  return (
    <div className="card card-pad" data-testid="study-plan-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="eyebrow inline-flex items-center gap-1"><Bot size={12} className="text-signal" /> AI daily study plan</span>
        <button className="btn-primary" onClick={gen} disabled={busy} data-testid="gen-plan-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {busy ? "Planning…" : plan ? "Regenerate" : "Plan my day"}
        </button>
      </div>
      {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
      {!plan && !busy && <p className="text-muted text-sm mt-3">Get a personalised plan built from your enrolled labs, weak topics and target role.</p>}
      {plan && (
        <div className="mt-4 space-y-3" data-testid="study-plan-result">
          <p className="font-display font-bold">{plan.focus}</p>
          <div className="space-y-2">
            {(plan.blocks || []).map(b => (
              <div key={b.task} className="flex gap-3 border border-rule rounded-xl p-3">
                <span className="text-[10px] font-bold uppercase self-start px-2 py-0.5 rounded-full shrink-0"
                      style={{ background: `${TYPE_COLOR[b.type] || "#006CFF"}15`, color: TYPE_COLOR[b.type] || "#006CFF" }}>
                  {b.type}
                </span>
                <div className="min-w-0">
                  <div className="text-xs text-muted">{b.time}</div>
                  <div className="text-sm font-semibold">{b.task}</div>
                  <div className="text-xs text-muted mt-0.5">{b.why}</div>
                </div>
              </div>
            ))}
          </div>
          {plan.weak_topic_fix && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3">
              <strong>Weak-topic fix:</strong> {plan.weak_topic_fix}
            </div>
          )}
          {plan.motivation && <p className="text-xs italic text-muted">{plan.motivation}</p>}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- weak topics */
function WeakTopics() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/coach/weak-topics").then(r => setRows(r.data.weak_topics)).catch(() => setRows([]));
  }, []);
  return (
    <div className="card card-pad" data-testid="weak-topics-card">
      <span className="eyebrow inline-flex items-center gap-1"><AlertTriangle size={12} className="text-amber-500" /> Weak topic detection</span>
      {rows === null ? <div className="text-muted text-sm mt-3">Analyzing…</div>
        : rows.length === 0 ? (
          <p className="text-muted text-sm mt-3" data-testid="no-weak-topics">No weak topics detected — every assessment you've taken is at 70%+. Keep going!</p>
        ) : (
          <div className="mt-3 space-y-2">
            {rows.map(w => (
              <CoeLabCardLink key={w.module_id}
                              labCode={w.lab_code}
                              fallbackInternalHref={w.lab_id ? `/lms/labs/${w.lab_id}` : "/student/lms"}
                              className="flex items-center justify-between gap-3 border border-rule rounded-xl px-3 py-2 hover:border-signal transition"
                              testid={`weak-topic-${w.module_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{w.module_title}</div>
                  <div className="text-[11px] text-muted">{w.lab_code} · {w.attempts} attempt{w.attempts === 1 ? "" : "s"}</div>
                </div>
                <span className="font-mono font-bold text-red-600 text-sm shrink-0">{w.best_score}%</span>
              </CoeLabCardLink>
            ))}
          </div>
        )}
    </div>
  );
}

/* --------------------------------------------------------- code conquest */
function CodeConquest() {
  const [rnd, setRnd] = useState(null);
  const [sel, setSel] = useState(null);
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/conquest/active").then(r => {
      setRnd(r.data);
      api.get(`/conquest/${r.data.id}/leaderboard`, { params: { limit: 5 } })
        .then(b => setBoard(b.data)).catch(() => {});
    }).catch(() => setRnd(false));
  }, []);
  const submit = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await api.post(`/conquest/${rnd.id}/submit`, { problem_id: sel.id, code, language: "python" });
      setRes(r.data);
    } catch (e) { setRes({ error: formatError(e) }); }
    finally { setBusy(false); }
  };
  if (rnd === false) return null;
  return (
    <div className="card card-pad" data-testid="conquest-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="eyebrow inline-flex items-center gap-1"><Code2 size={12} className="text-signal" /> Code Conquest · weekly</span>
        {rnd && <span className="text-[11px] text-muted font-mono">{rnd.title} · best: {rnd.my_best_score ?? 0}</span>}
      </div>
      {rnd === null ? <div className="text-muted text-sm mt-3">Loading…</div> : (
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            {rnd.problems.map(p => (
              <button key={p.id} onClick={() => { setSel(p); setRes(null); }}
                      className={`w-full text-left border rounded-xl px-3 py-2 transition ${sel?.id === p.id ? "border-signal bg-signal/5" : "border-rule hover:border-signal/40"}`}
                      data-testid={`conquest-problem-${p.id}`}>
                <div className="text-sm font-semibold">{p.title}</div>
                <div className="text-[11px] text-muted line-clamp-2 whitespace-pre-wrap">{p.prompt}</div>
              </button>
            ))}
            {board.length > 0 && (
              <div className="border border-rule rounded-xl p-3 mt-2">
                <div className="text-xs font-semibold inline-flex items-center gap-1 mb-2"><Trophy size={12} className="text-amber-500" /> Leaderboard</div>
                {board.map(r => (
                  <div key={r.user_id} className="flex justify-between text-xs py-0.5">
                    <span className="text-muted">#{r.rank} {r.name}</span><span className="font-mono font-bold">{r.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            {sel ? (
              <>
                <textarea className="input font-mono text-xs min-h-[180px] w-full"
                          placeholder={"# Python solution\n# read from stdin, print to stdout"}
                          value={code} onChange={e => setCode(e.target.value)}
                          data-testid="conquest-code" />
                <button className="btn-primary mt-2" onClick={submit} disabled={busy || !code.trim()} data-testid="conquest-submit">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Code2 size={14} />}
                  {busy ? "Grading…" : "Submit solution"}
                </button>
                {res && (
                  <div className="mt-2 text-sm" data-testid="conquest-result">
                    {res.error ? <span className="text-red-600">{res.error}</span>
                      : <span className={res.score >= 100 ? "text-green-700 font-semibold" : "font-semibold"}>Score: {res.score}/{res.max_score}</span>}
                  </div>
                )}
              </>
            ) : <p className="text-muted text-sm">Pick a problem to start coding →</p>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ page */
export default function StudentCoach() {
  usePageTitle("AI Learning Coach");
  return (
    <div className="space-y-6" data-testid="coach-page">
      <div className="grid md:grid-cols-2 gap-6">
        <DailyQuiz />
        <StreakCard />
      </div>
      <StudyPlan />
      <WeakTopics />
      <CodeConquest />
    </div>
  );
}
